import { Container } from "@pixi/react";
import React, { useMemo, useRef, useCallback, useEffect } from "react";
import { Settings } from "../../settings";
import { useDimensions } from "../../Contexts/DimensionsContext";
import HoldAndWinReel from "./HoldAndWinReel";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../assets";
import {
  selectHoldAndWinInitialReels,
  selectIsHoldAndWinMidFeatureReload
} from "../../State/selectors";
import { useSelector } from "react-redux";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import { useHoldAndWinControl } from "../../Contexts/HoldAndWinControlContext";
import { useHoldAndWinIdleAnimations } from "../../Hooks/useLandingAnimations";

const HoldAndWinReelSet = () => {
  const { symbolHeight, symbolWidth, reelSpacing } = useDimensions();
  const initialReels = useSelector(selectHoldAndWinInitialReels);
  const isHoldAndWinMidFeatureReload = useSelector(selectIsHoldAndWinMidFeatureReload);
  const { holdAndWinReelRefs } = useReelRefs();
  const { handleSymbolStopComplete } = useHoldAndWinControl();
  const { triggerHoldAndWinIdleAnimations } = useHoldAndWinIdleAnimations();
  const reelRefs = useRef([]);
  const idleAnimationsTriggeredRef = useRef(false);

  // Callback ref to register reel with context
  const setReelRef = useCallback(
    (index) => (el) => {
      reelRefs.current[index] = el;
      if (holdAndWinReelRefs.current) {
        holdAndWinReelRefs.current[index] = el;
      }
    },
    [holdAndWinReelRefs]
  );

  const symbolsSpriteSheet = useMemo(
    () => PIXI.Assets.get(ASSET_NAME.reel_symbols),
    []
  );
  const symbolsSpriteSheetBlurred = useMemo(
    () => PIXI.Assets.get(ASSET_NAME.reel_symbols_blurred),
    []
  );

  // Extract reelSymbolsData like in main ReelSet for consistency and performance
  const reelSymbolsData = useMemo(() => {
    if (!symbolsSpriteSheet) return null;
    return (
      symbolsSpriteSheet.data?.frames || symbolsSpriteSheet.textures || null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CRITICAL: Trigger idle animations for MID-FEATURE reload only
  // Intro reload is handled by scene transition (has proper timing)
  // Mid-feature reload has no scene transition, so we trigger here
  useEffect(() => {
    if (isHoldAndWinMidFeatureReload && !idleAnimationsTriggeredRef.current && initialReels && initialReels.length > 0) {
      // Use multiple RAF to ensure overlay symbols are fully mounted
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            triggerHoldAndWinIdleAnimations();
            idleAnimationsTriggeredRef.current = true;
          });
        });
      });
    }

    // Reset flag when reload flags clear
    if (!isHoldAndWinMidFeatureReload && idleAnimationsTriggeredRef.current) {
      idleAnimationsTriggeredRef.current = false;
    }
  }, [initialReels, isHoldAndWinMidFeatureReload, triggerHoldAndWinIdleAnimations]);

  // Guard: Don't render until initialReels are ready
  if (
    !initialReels ||
    !Array.isArray(initialReels) ||
    initialReels.length === 0
  ) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_reelset"
      zIndex={6}
      x={0}
      y={-symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5)}
    >
      {initialReels.map((reel, index) => (
        <HoldAndWinReel
          key={index}
          ref={setReelRef(index)}
          reel={reel}
          index={index}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
          symbolsSpriteSheet={symbolsSpriteSheet}
          symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
          reelSymbolsData={reelSymbolsData}
          onSymbolStopComplete={handleSymbolStopComplete}
          holdAndWinReelRefs={holdAndWinReelRefs}
        />
      ))}
    </Container>
  );
};

export default HoldAndWinReelSet;
